// Node imports
import { View, SlotConstantsBehavior, SlotEventConstantsBehavior } from "@quest/murka-games-behavior-leopard/dist";
import { IEvent, IObject, ITween, Linear, SpineBase, dispatcher, ingenuity, Container, detectDeviceEnvironmentConstants } from "@quest/base-core";
import { ReelPanel, SlotConstants, StaticSymbol, SymbolBase } from "@quest/slot-core";

//  Class imports
import { gameConstants } from "../Constants/gameConstants";
import { SoundConstantGame } from "../Constants/SoundConstantGame";
import { eventDispatchers } from "../Constants/EventDispatchers";
import { GameParserModel } from "../Platform/GameParserModel";

/**
 * BaseGameViewGame class serves as the foundation for managing the visual aspects
 * of the game, encompassing rendering, display, and presentation logic.
 * It establishes a fundamental structure for handling visual elements within the game,
 * offering a starting point for creating more specialized views.
 */
export class BaseGameViewGame extends View {

  public scatterSymbolCount: number = 0;
  private reelId_0: number = 0;
  private reelId_2: number = 2;
  private reelId_4: number = 4;
  private scatterSymbol_0: number = 0;
  private scatterSymbol_1: number = 1;
  private scatterSymbol_2: number = 2;
  private freePlayView: any;

  /**to hide basegame view behind loading screen*/
  constructor(json: any) {
    super(json);
    this.visible = false;
    this.getContainerByID("buttonsContainer") && (this.getContainerByID("buttonsContainer").visible = false);
  }

  /**
   * subscribeEvents method overrided to subscribe game end events
   */
  protected subscribeEvents(): void {
    super.subscribeEvents();
    dispatcher.on(eventDispatchers.RESET_SCATTER_DATA, this.resetScatterSymbolData, this);
    dispatcher.on(eventDispatchers.HIDE_POTION_LANDING_SYMBOL, this.hidePotionLandingAnim, this);
    dispatcher.on(eventDispatchers.SET_FREEPLAY_VIEW_LAYERING, this.setFreePlayView, this);
  }
  
  /**
   * unsubscribeEvents method overrided to unSubscribe game end events
  */
 protected unsubscribeEvents(): void {
   super.unsubscribeEvents();
   dispatcher.off(eventDispatchers.RESET_SCATTER_DATA, this.resetScatterSymbolData, this);
   dispatcher.off(eventDispatchers.HIDE_POTION_LANDING_SYMBOL, this.hidePotionLandingAnim, this);
   dispatcher.off(eventDispatchers.SET_FREEPLAY_VIEW_LAYERING, this.setFreePlayView, this);
  }


  // Override to play scatter and pot symbol landing animation
  public landingAnimation(data: any) {
    const reelgrid: [][] = data[0];
    const reelview: ReelPanel = data[1];
    const reelId: number = data[2];
    for (let i = 0; i < reelgrid[reelId].length; i++) {
      if (this.json.reels.symbolData[reelgrid[reelId][i]].hasLanding) {
        const symbol: SymbolBase = reelview.getSymbolByPos(reelId, i);
        const staticSym: StaticSymbol = symbol.getStatic() as StaticSymbol;
        const animSym: SpineBase = symbol.getAnimation() as SpineBase;

        if (staticSym.name === gameConstants.FS_SYMBOL) {
          if (reelgrid[reelId][i] === gameConstants.FREE_SPIN_SYMBOL) {
            if (reelId === this.reelId_0 && this.scatterSymbolCount === this.scatterSymbol_0) {
              this.playScatterLandingAnim(staticSym, animSym, reelId);
            }
            else if (reelId === this.reelId_2 && this.scatterSymbolCount === this.scatterSymbol_1) {
              this.playScatterLandingAnim(staticSym, animSym, reelId);
            }
            else if (reelId === this.reelId_4 && this.scatterSymbolCount === this.scatterSymbol_2) {
              this.playScatterLandingAnim(staticSym, animSym, reelId);

            }
          }
        }

        //play landing animation for pot symbol
        if (reelgrid[reelId][i] === gameConstants.POT_SYMBOL && staticSym.name === gameConstants.POT_SYMBOL_NAME) {
          if (animSym) {
            const staticSymParent: IObject = staticSym.parent;
            if (staticSymParent) {
              staticSym.visible = false;
              animSym.visible = true;
              staticSymParent.addChild(animSym);
              animSym.playAnim("white_idle", false);
              animSym.onComplete.addOnce(() => {
                animSym.playAnim("white", false);
              }, this);
            }
          }
        }
      }
    }
  }

  /**
   * Method to play scatter landing animation
   */
  private playScatterLandingAnim(staticSym: StaticSymbol, animSym: SpineBase, reelId: number): void {
    this.scatterSymbolCount = this.scatterSymbolCount + 1;
    const staticSymParent = staticSym.parent;
    if (staticSymParent && animSym) {
      (ingenuity.soundManager as any).playSound(SoundConstantGame.SCATTER_LANDING + (reelId), 1, false, gameConstants.SOUND_LIST_PRELOAD);
      staticSym.visible = false;
      animSym.visible = true;
      staticSymParent.addChild(animSym);
      animSym.playAnim("static", false);
      animSym.onComplete.addOnce(() => {
        animSym.playAnim("landing", false);
      }, this);
    }
  }

  /**
 * Method to reset scatter symbol count 
 */
  private resetScatterSymbolData(): void {
    this.scatterSymbolCount = 0;
  }
  /**
   * function is over rided to play spine animation for anticipation
   * initially the animation for anticipation was for sprite
   */
  public showAnticipation(reel: number, callback?: () => void, callbackScope?: any): void {
    if (typeof this.anticipationContainer === "undefined") {
      this.anticipationContainer = new Container(this.json, this.game);
      this.anticipationContainer.name = SlotConstants.AnticipationConatiner;
      this.addChild(this.anticipationContainer);
    }
    if (this.json.anticipationAnim) {
      const anticJson = this.json.anticipationAnim;
      anticJson.id = "anticipation";
      anticJson.type = "spine";
      const anticipation = this.createSpine(anticJson);
      if (anticipation) {
        anticipation.visible = true;
        this.anticipationContainer.addChild(anticipation);
        this.anticipationContainer.x = this.json.reels.reels[reel].x + this.json.reels.x + this.json.anticipationAnim.valueX;
        this.anticipationContainer.y = this.json.reels.reels[reel].y + this.json.reels.y + this.json.anticipationAnim.valueY;
        this.addChild(this.anticipationContainer);
        anticipation.playAnim("anticipation", true);
      }
    }
  }


  /**
   * Override to hide animation with fade out effect
   */
  public clearAnticipation(): void {
    if (typeof this.anticipationContainer !== "undefined" && this.anticipationContainer.children[0]) {
      const fadeOutEffect: ITween = ingenuity.currentGame.add.tween(this.anticipationContainer.children[0]).to({ alpha: gameConstants.ALPHA_0 }, gameConstants.TIMER_200, Linear.None, true, 0, 0, false);
      fadeOutEffect.onComplete.addOnce(() => {
        ingenuity.currentGame.tweens.remove(fadeOutEffect);
        this.anticipationContainer.children[0] && this.anticipationContainer.removeChildAt(0);
      }, this);
    }
  }

  /*
 Override to remove play landing sounds from here
 */
  protected checkAndPlayLandingSounds(evt: IEvent): void {

  }

  /**
   * This method is use to hide potion bag static symbol when its animation plays
   */
  private hidePotionLandingAnim(evt?: IEvent): void {
    const symbol: SymbolBase = this.getReelView().getSymbolByPos(evt.data[0], evt.data[1]);
    const staticSym: StaticSymbol = symbol.getStatic() as StaticSymbol;
    const animSym: SpineBase = symbol.getAnimation() as SpineBase;
    staticSym.visible = false;
    animSym.visible = false;
  }
  protected addButtonPanelOnTop() {
    this.getContainerByID("portraitButtonsContainer") && ingenuity.currentGame.stage.addChild(this.getContainerByID("portraitButtonsContainer"));
    this.getContainerByID("topPortraitButtonsContainer") && ingenuity.currentGame.stage.addChild(this.getContainerByID("topPortraitButtonsContainer"));

    const buttonsContainer = this.getContainerByID("buttonsContainer");
    ingenuity.currentGame.stage.addChild(buttonsContainer);
    const buttonsContainerWidth = buttonsContainer.width;
    if (ingenuity.deviceEnv.getOrientation() === detectDeviceEnvironmentConstants.constants.ORIENTATION.LANDSCAPE) {
      buttonsContainer.pivot.set(ingenuity.configData.width / 2, 0);
      const scale = Math.min((window.innerHeight / ingenuity.configData.height), (window.innerWidth / ingenuity.configData.width));
      buttonsContainer.x = (buttonsContainer.width / 2) + ((window.innerWidth - buttonsContainerWidth) / 2);
      buttonsContainer.y = window.innerHeight - (this.getImageById("Button_Panel_Base").height * scale);
      buttonsContainer.scale.set(scale, scale);
    }
    else if (ingenuity.deviceEnv.getOrientation() === detectDeviceEnvironmentConstants.constants.ORIENTATION.PORTRAIT) {
      buttonsContainer.scale.set(this.portraitPanelSCaling());
      const bottomPanelBgPortrait = this.getImageById("Button_Panel_BasePort");
      bottomPanelBgPortrait && buttonsContainer.pivot.set(bottomPanelBgPortrait.width / 2, bottomPanelBgPortrait.height);
      buttonsContainer.x = window.innerWidth / 2;
      buttonsContainer.y = window.innerHeight;
    }
    dispatcher.fireEvent(eventDispatchers.ADD_TOP_BAR_ON_TOP);
  }
  public onShowPortraitButtonpanel() {
    if (this.PortraitplayerMsgContainer && this.portraitButtonsContainer) {
      this.PortraitplayerMsgContainer.visible = true;
      this.PortraitplayerMsgContainer.y = this.getImageById("reelBG").y + this.getShapeById("baseGameResizer").height + SlotConstantsBehavior.PLAYER_MSSG_HEIGHT_SAFEAREA;
      dispatcher.fireEvent(SlotEventConstantsBehavior.ON_CHANGE_PLAYERMSG_PARENT);
      this.changeParent(this.portraitButtonsContainer);
      const portraitButtonContainerBg = this.getShapeById("portraitBackgroundShape");
      this.portraitButtonsContainer.scale.set(this.portraitPanelSCaling());
      let portraitButtonContainerBgHeight = (portraitButtonContainerBg.height * this.portraitButtonsContainer.scale.x);
      let meterPanelHeight = this.getImageById("Button_Panel_BasePort").height * this.getContainerByID("buttonsContainer").scale.x
      this.portraitButtonsContainer.pivot.set(ingenuity.configData.width / 2, 0);
      this.portraitButtonsContainer.x = window.innerWidth / 2;
      this.portraitButtonsContainer.y = window.innerHeight - (portraitButtonContainerBgHeight + meterPanelHeight);
      this.portraitButtonsContainer.alpha = 1;
    }
  }
  protected portraitPanelSCaling(): number {
    const viewHeight = (this.getShapeById("baseGameResizer").height / 2) * this.scale.x;
    let remainingHeight = (window.innerHeight / 2) - viewHeight;
    const portraitButtonContainerBg = this.getShapeById("portraitBackgroundShape");
    let availableScaling = this.scalingAvailable(remainingHeight, portraitButtonContainerBg);
    if (availableScaling > SlotConstantsBehavior.FLOATING_MAX_SCALE) {
      availableScaling = SlotConstantsBehavior.FLOATING_MAX_SCALE;
    }
    return availableScaling;
  }
  public resizeOrientation(e: IEvent) {
    this.storeLandscapeReelSize();//do not change order
    super.resizeOrientation(e);
    if (ingenuity.deviceEnv.getOrientation() !== detectDeviceEnvironmentConstants.constants.ORIENTATION.PORTRAIT) {
      this.scale.set(ingenuity.deviceEnv.getLandcapeMaxReelScale())
      this.y = (innerHeight * SlotConstantsBehavior.HALF_CONVERTER) + ((gameConstants.REEL_BOTTOM_SPACE / 2) * this.scale.x);
      if (!ingenuity.deviceEnv.isDesktop) {
        this.y = (innerHeight * SlotConstantsBehavior.HALF_CONVERTER) + ((gameConstants.REEL_BOTTOM_SPACE_LANDSCAPE / 2) * this.scale.x);
      }
    }
  }
  protected storeLandscapeReelSize(): void {
    gameConstants.REEL_WIDTH = this.getShapeById("baseGameLandscapeResizer").width;
    gameConstants.REEL_HEIGHT = this.getShapeById("baseGameLandscapeResizer").height;
  }
  protected portraitHudOnResize() {
    if (ingenuity.deviceEnv.getOrientation() === detectDeviceEnvironmentConstants.constants.ORIENTATION.PORTRAIT) {
      this.onShowPortraitButtonpanel();
    }
    else {
      if (!ingenuity.deviceEnv.isDesktop) {
        this.onHidePortraitButtonPanel();
        this.getContainerByID("buttonsContainer").addChild(this.getContainerByID("lightningAnimContainer"));
      }
    }
  }
  // Overrided to stop hide help in landscape
  protected hideHelpInMobileLandscape(): void {
    //
  }
  public resize(e?: IObject): void {
    dispatcher.fireEvent(eventDispatchers.RESIZE_SETTING_PANEL)
    super.resize(e);

    this.changeFreePlayViewParent();
    dispatcher.fireEvent(SlotEventConstantsBehavior.SHOW_TOP_GAME_LOGO);
  }
  private setFreePlayView(evt:IEvent) {
    this.freePlayView = evt.data;
    this.changeFreePlayViewParent();
  }
  /**
   * to add freeplay view on button panel pola
   */
  private changeFreePlayViewParent() {
    if (this.freePlayView && (ingenuity.parserModel as GameParserModel).getPromotionalFreeGame()) {
      if (ingenuity.deviceEnv.getOrientation() === detectDeviceEnvironmentConstants.constants.ORIENTATION.PORTRAIT) {
        this.getContainerByID("portraitButtonsContainer").addChild(this.freePlayView);
      }
      else {
        this.getContainerByID("buttonsContainer").addChild(this.freePlayView);
      }
    }
  }
}
